/**
 * Routes — the thing a fab actually runs.
 *
 * Every tool in this codebase has been built as a place you walk up to. A
 * real fab is not organised that way. A product is a ROUTE: an ordered list of
 * three or four hundred operations, and a wafer visits the same etcher eleven
 * times on its way down it. "Step 47 of 312" is how work is scheduled, how
 * problems are located, and how a technician is told what to do next.
 *
 * WHY THIS NEEDS TO BE A REAL ABSTRACTION
 *
 * Some quantities do not belong to any tool. They belong to the route:
 *
 *   THERMAL BUDGET  every hot step spends from one account, and the junction
 *                   formed at step 15 is still moving at step 200. The last
 *                   anneal in a flow is constrained by every anneal before it.
 *
 *   DEFECT DENSITY  every operation is a chance to add a particle. A process
 *                   module with more steps is a process module with lower
 *                   yield, and no single tool in it is doing anything wrong.
 *
 *   MASK COUNT      a lithography step needs a reticle, and reticles are the
 *                   most expensive thing in the building. Whole architectural
 *                   decisions exist to trade masks against steps.
 *
 *   COST            capital divided by hours. An EUV scanner and an etcher
 *                   differ by a factor of thirty in what an hour of them is
 *                   worth, which is why the cheap tool runs eight times.
 *
 * None of those can be taught at a tool. All of them are obvious on a route.
 *
 * WHAT THIS MAKES POSSIBLE
 *
 * The question that decided the last decade of the industry - EUV or
 * multipatterning - is a route comparison and nothing else. Both print the same
 * pitch. One uses one expensive exposure and two operations; the other uses one
 * cheap exposure and eight. Which is better is arithmetic over a route, and
 * this module is where that arithmetic lives.
 */

import { negativeBinomialYield } from '../sort/ProbeSim'

/**
 * What an hour of each tool is worth [USD/hour].
 *
 * Not a survey - a calculation. Capital cost spread over a five-year
 * depreciation at 85% utilisation:
 *
 *     rate = capex / (5 x 8760 h x 0.85)
 *
 * which is why the numbers are so far apart: an EUV scanner is a 180 million
 * dollar machine and an etch chamber is a six million dollar one. Everything
 * about how a fab sequences work follows from that ratio.
 */
const CAPEX_MUSD: Record<string, number> = {
  'GOWN-00': 2,
  'ETCH-01': 6,
  'DEP-02': 4,
  'LITH-03': 80,
  // The EUV scanner is a station of its own since EUV-22 was built; it used
  // to be a second price on LITH-03's bay (`LITH-03-EUV`).
  'EUV-22': 180,
  'EUV-22-HIGHNA': 380,
  'SEM-23': 9,
  'QDT-24': 3,
  'THRM-04': 3,
  'MET-05': 7,
  'FAC-06': 1,
  'CZ-07': 12,
  'WPR-08': 9,
  'CLN-09': 3,
  'EPI-20': 7,
  'IMP-10': 8,
  'CMP-11': 5,
  'ECD-12': 4,
  'INSP-13': 25,
  'PRB-14': 12,
  'DICE-15': 2,
  'BOND-16': 3,
  'PKG-17': 4,
  'FT-18': 9,
  'GAS-19': 2,
  'PAT-21': 0,
}

/**
 * What a reticle costs [USD].
 *
 * The piece the tool-time model was missing, and the one that makes mask
 * count matter at all. A photomask for an advanced node is a hand-built
 * object that takes weeks, and an EUV mask - a multilayer reflector with a
 * patterned absorber, defect-free at 13.5 nm - costs several times a DUV one.
 *
 * It is a fixed cost per LAYER, spread over every wafer the product ever
 * runs. That is why mask count is an architectural decision and step count is
 * an operational one: a step costs you every wafer, a mask costs you once.
 */
const RETICLE_USD: Record<string, number> = {
  'LITH-03': 150_000,
  'EUV-22': 350_000,
  'EUV-22-HIGHNA': 500_000,
}

export const reticleCost = (tool: string): number => RETICLE_USD[tool] ?? 120_000

const DEPRECIATION_YEARS = 5
const UTILISATION = 0.85

/** Tool cost per hour [USD/h], from capital. */
export const toolRatePerHour = (tool: string): number =>
  ((CAPEX_MUSD[tool] ?? 4) * 1e6) / (DEPRECIATION_YEARS * 8760 * UTILISATION)

/** One operation on a route. */
export interface FlowStep {
  /** Asset tag of the tool that runs it. */
  tool: string
  label: string
  /** Why it is in the route at all, in one line. */
  purpose: string
  /** Wafers per hour through this step. Sets both cost and cycle time. */
  wafersPerHour: number
  /** True when this step exposes a reticle. */
  mask?: boolean
  /**
   * Thermal budget spent, as equivalent seconds at 1000 C.
   *
   * The currency THRM-04 already deals in, so a route can add up what several
   * furnace visits cost together - which is the number no single visit shows.
   */
  thermalSeconds?: number
  /** Particles this operation adds [per cm^2]. Every touch costs something. */
  particleAddersCm2?: number
  /** Probability a wafer is scrapped or broken here. Small, and it compounds. */
  scrapRisk?: number
}

/** A route node: either one operation, or a whole sub-route inlined. */
export type FlowNode = FlowStep | { flow: ProcessFlow; repeat?: number }

export interface ProcessFlow {
  id: string
  name: string
  /** What this route is for, in one line. */
  purpose: string
  nodes: FlowNode[]
}

const isStep = (n: FlowNode): n is FlowStep => (n as FlowStep).tool !== undefined

/**
 * Flatten a route, inlining sub-routes.
 *
 * A patterning module is a route that appears inside a layer route that appears
 * inside a product route, and a technician standing at a tool needs the flat
 * list - "step 47 of 312" - not the tree. Both views are the same object.
 */
export function expand(flow: ProcessFlow): FlowStep[] {
  const out: FlowStep[] = []
  for (const node of flow.nodes) {
    if (isStep(node)) {
      out.push(node)
    } else {
      const times = node.repeat ?? 1
      for (let i = 0; i < times; i++) out.push(...expand(node.flow))
    }
  }
  return out
}

export interface RouteResult {
  steps: FlowStep[]
  stepCount: number
  /** Reticle exposures. The most expensive thing on the route. */
  maskCount: number
  /** Tool time per wafer [hours] - not cycle time, which includes queueing. */
  toolHours: number
  /** Cost per wafer of the tool time [USD]. */
  costUsd: number
  /** Cost of the litho steps alone [USD], and their share. */
  lithoCostUsd: number
  lithoShare: number
  /** Of which, the amortised reticles [USD]. */
  reticleCostUsd: number
  /** Thermal budget spent [equivalent seconds at 1000 C]. */
  thermalSeconds: number
  /** Particles added along the route [per cm^2]. */
  addedDefectsCm2: number
  /**
   * Line yield: the fraction of wafers that survive the route at all.
   *
   * Distinct from die yield and constantly confused with it. This is breakage,
   * misprocessing and scrap - whole wafers lost - and because it compounds over
   * every step, a route long enough will lose wafers at a per-step rate that
   * looks like nothing.
   */
  lineYield: number
  /** Die yield from the defects this route added, at a given die size. */
  dieYield: number
  /** Both together - what actually comes out. */
  compositeYield: number
}

export interface RouteOptions {
  /** Die area for the yield calculation [cm^2]. */
  dieAreaCm2: number
  /** Defect density the wafer arrives with [per cm^2]. */
  incomingDefectsCm2: number
  /** Clustering parameter for the negative binomial. */
  clusterAlpha: number
  /**
   * Wafers this mask set will ever print.
   *
   * The denominator that decides whether a mask is expensive. At high volume
   * a reticle is nothing per wafer and step count is everything; on a short
   * run it is the dominant cost and the calculus inverts. Which is why the
   * same process can be the right answer for one product and the wrong one
   * for another with identical geometry.
   */
  waferVolume: number
}

export const DEFAULT_ROUTE_OPTIONS: RouteOptions = {
  dieAreaCm2: 0.59,
  incomingDefectsCm2: 0.02,
  clusterAlpha: 2,
  waferVolume: 50_000,
}

export function runRoute(
  flow: ProcessFlow,
  options: RouteOptions = DEFAULT_ROUTE_OPTIONS,
): RouteResult {
  const steps = expand(flow)

  let maskCount = 0
  let toolHours = 0
  let costUsd = 0
  let lithoCostUsd = 0
  let reticleCostUsd = 0
  let thermalSeconds = 0
  let addedDefectsCm2 = 0
  let lineYield = 1

  for (const s of steps) {
    const hours = 1 / Math.max(s.wafersPerHour, 1e-9)
    const cost = hours * toolRatePerHour(s.tool)
    toolHours += hours
    costUsd += cost
    if (s.mask) {
      maskCount++
      // The reticle, spread over every wafer it will ever print.
      const amortised = reticleCost(s.tool) / Math.max(options.waferVolume, 1)
      costUsd += amortised
      lithoCostUsd += cost + amortised
      reticleCostUsd += amortised
    }
    thermalSeconds += s.thermalSeconds ?? 0
    addedDefectsCm2 += s.particleAddersCm2 ?? 0
    // Every step is a chance to lose the wafer, and the losses multiply.
    lineYield *= 1 - (s.scrapRisk ?? 0)
  }

  const totalDefects = options.incomingDefectsCm2 + addedDefectsCm2
  const dieYield = negativeBinomialYield(totalDefects, options.dieAreaCm2, options.clusterAlpha)

  return {
    steps,
    stepCount: steps.length,
    maskCount,
    toolHours,
    costUsd,
    lithoCostUsd,
    lithoShare: costUsd > 0 ? lithoCostUsd / costUsd : 0,
    reticleCostUsd,
    thermalSeconds,
    addedDefectsCm2,
    lineYield,
    dieYield,
    compositeYield: lineYield * dieYield,
  }
}

/**
 * Cost per GOOD die, which is the only cost figure that means anything.
 *
 * A route that is cheaper per wafer and worse per die can easily be the more
 * expensive of the two, and this is the arithmetic that says which. It is also
 * the number that settles the argument this module was built to settle.
 */
export function costPerGoodDie(
  flow: ProcessFlow,
  options: RouteOptions = DEFAULT_ROUTE_OPTIONS,
): { result: RouteResult; grossDie: number; goodDie: number; usdPerGoodDie: number } {
  const result = runRoute(flow, options)
  // Gross die on a 300 mm wafer, with the usual edge allowance.
  const usableAreaCm2 = Math.PI * 14.5 * 14.5
  const grossDie = Math.floor(usableAreaCm2 / options.dieAreaCm2)
  const goodDie = grossDie * result.compositeYield
  return {
    result,
    grossDie,
    goodDie,
    usdPerGoodDie: goodDie > 0 ? result.costUsd / goodDie : Infinity,
  }
}

/** Where the route spends its money, by tool. */
export function costByTool(flow: ProcessFlow): Array<{ tool: string; usd: number; visits: number }> {
  const byTool = new Map<string, { usd: number; visits: number }>()
  for (const s of expand(flow)) {
    const hours = 1 / Math.max(s.wafersPerHour, 1e-9)
    const entry = byTool.get(s.tool) ?? { usd: 0, visits: 0 }
    entry.usd += hours * toolRatePerHour(s.tool)
    entry.visits++
    byTool.set(s.tool, entry)
  }
  return [...byTool.entries()]
    .map(([tool, v]) => ({ tool, ...v }))
    .sort((a, b) => b.usd - a.usd)
}

/**
 * How many times the wafer comes back to each tool.
 *
 * The number that surprises people who have only seen a floor plan: a wafer
 * visits the etcher more times than there are etch steps in any one module,
 * because it goes round the loop once per layer.
 */
export function visitCounts(flow: ProcessFlow): Array<{ tool: string; visits: number }> {
  const counts = new Map<string, number>()
  for (const s of expand(flow)) counts.set(s.tool, (counts.get(s.tool) ?? 0) + 1)
  return [...counts.entries()]
    .map(([tool, visits]) => ({ tool, visits }))
    .sort((a, b) => b.visits - a.visits)
}
